/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import cx from 'classnames';

import ToastContainer from '@broadleaf/admin-components/dist/common/components/ToastContainer';
import ItemBrowserBody from '../ItemBrowserBody';
import ItemBrowserHeader from '@broadleaf/admin-components/dist/form/item-browser/components/ItemBrowserHeader';
import type { IMetadataAction } from '@broadleaf/admin-components/dist/types/metadata';

export const ItemBrowser: React.FC<ItemBrowserProps> = props => {
  return (
    <div
      className={cx(
        'ItemBrowser',
        {
          'tw-h-[680px]': props.className?.indexOf('tw-h') === -1,
          [props.className]: !!props.className
        },

        'tw-relative tw-flex tw-w-full tw-flex-col tw-flex-nowrap tw-justify-start tw-bg-gray-100'
      )}
    >
      <ItemBrowserHeader
        actionComponents={props.actionComponents}
        actionDefinitions={props.actionDefinitions}
        enableFolders={props.enableFolders}
        folderIdKey={props.folderIdKey}
        title={props.headerTitle}
      />
      <ItemBrowserBody
        actionDefinitions={props.actionDefinitions}
        altKey={props.altKey}
        embedCodeKey={props.embedCodeKey}
        enableDetails={props.enableDetails}
        enableDetailsUpdates={props.enableDetailsUpdates}
        enableFolders={props.enableFolders}
        enableUploads={props.enableUploads}
        handleUpdate={props.handleUpdate}
        mimeTypeKey={props.mimeTypeKey}
        primaryKey={props.primaryKey}
        rowComponent={props.rowComponent}
        rowHeaderComponent={props.rowHeaderComponent}
        rowProps={props.rowProps}
        srcKey={props.srcKey}
        tagsKey={props.tagsKey}
        tileComponent={props.tileComponent}
        tileProps={props.tileProps}
        titleKey={props.titleKey}
        typeKey={props.typeKey}
      />
      <ToastContainer />
    </div>
  );
};

ItemBrowser.defaultProps = {
  altKey: 'alt',
  embedCodeKey: 'embedCode',
  enableDetails: true,
  enableDetailsUpdates: true,
  enableFolders: false,
  enableUploads: true,
  mimeTypeKey: 'mimeType',
  primaryKey: 'primary',
  srcKey: 'url',
  tagsKey: 'tags',
  titleKey: 'title',
  typeKey: 'type'
};

export interface ItemBrowserProps {
  /**
   * Array of action information with components. Used for providing
   * the components that should be rendered for action definitions.
   */
  actionComponents?: Array<IMetadataAction>;
  /** The actions for either the filters, item icon bar, global icon bar, or other action components. */
  actionDefinitions?: Array<IMetadataAction>;
  /** The property key for getting an item's alt value */
  altKey?: string;
  className?: string;
  /** The property key for getting an item's embed code value. This is used for item's without a source url and instead contain embedded content.  */
  embedCodeKey?: string;
  /** Enables the details view to display when an item is selected. */
  enableDetails?: boolean;
  /** Enables the details view to render a form that allow updates */
  enableDetailsUpdates?: boolean;
  /** Enables the folder view to display. */
  enableFolders?: boolean;
  /** Enables an upload dropzone to be rendered for the ItemBrowserBody */
  enableUploads?: boolean;
  folderIdKey?: string;
  /** Helper function used to trigger an assignment of an object to create an item */
  handleAssign?: Function;
  /** Helper function used to create an item */
  handleCreate?: Function;
  /** Helper function used to create an folder */
  handleCreateFolder?: Function;
  /** Helper function used to delete an item */
  handleDelete?: Function;
  /** Helper function used to delete an folder */
  handleDeleteFolder?: Function;
  /** Helper function used to trigger a read of data */
  handleRead?: Function;
  /** Helper function used to trigger a read of the root folders */
  handleReadRootFolders?: Function;
  /** Helper function used to trigger a read of a folder's sub-folders */
  handleReadSubFolders?: Function;
  /** Helper function used to trigger an update of an existing item */
  handleUpdate?: Function;
  /** Helper function used to trigger an update of an existing folder */
  handleUpdateFolder?: Function;
  /** Helper function used to trigger an upload of a new item */
  handleUpload?: Function;
  /** Title for the ItemBrowserHeader component */
  headerTitle: string;
  /** The property key for getting an item's ID value */
  idKey?: string;
  /** The property key for getting an item's mimeType value */
  mimeTypeKey?: string;
  /** The property key for getting an item's primary value, if it exists */
  primaryKey?: string;
  /** The property key for getting an item's src value */
  srcKey?: string;
  rowComponent?: React.ReactNode;
  rowHeaderComponent?: React.ReactNode;
  rowProps?: object;
  /** The property key for getting an item's tags value */
  tagsKey?: string;
  /** The property key for getting an item's title value */
  tileComponent?: React.ReactNode;
  tileProps?: object;
  titleKey?: string;
  /** The property key for getting an item's type value */
  typeKey?: string;
}

export default ItemBrowser;
